import React, { useState } from "react";
import { Box, Button, CircularProgress, Modal, TextField, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import apiClient from "../../axiosClient";
import { useNavigate } from "react-router-dom";
import { decodedJWT, getUserName } from "../../utils/commonUtils";
import { passwordPattern } from "../../constants/patterns";
import JWTTokenPayload from "../../interfaces/JWTTokenPayload";
import { AppDispatch } from "../../store/store";
import { useDispatch } from "react-redux";
import clearReduxAndTokenThunk from "../../store/thunks/clearReduxAndTokenThunk";
import { protectedTextFieldProps } from "../../utils/protectedTextFieldProps";

interface ResetPasswordModalProps {
    openModal: boolean,
    username: String,
    handleCloseModal: () => void,
    isNonDismissable?: boolean
}

const styles = {
    modalStyle: {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '40%',
        bgcolor: 'background.paper',
        borderRadius: 4,
        boxShadow: 24,
        p: 4,
        overflowY: 'auto'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        marginBottom: 3,
    },
    input: {
        marginBottom: 2,
    },
};

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({ openModal, username, handleCloseModal, isNonDismissable = false }) => {
    const navigate = useNavigate();
    const dispatch: AppDispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [oldPassword, setOldPassword] = useState<string>('');
	const token: JWTTokenPayload|false  = decodedJWT();

    const closeModal = () => {
        setNewPassword('');
        setConfirmPassword('');
        setOldPassword('');
        handleCloseModal();
    };

    const closeModalSuccess = () => {
        closeModal();
        if (username === getUserName(token)) {
            dispatch(clearReduxAndTokenThunk(true));
            navigate('/login');
        }
    };

    const submit = async () => {
        try {
            if (!oldPassword) {
                alert('Error: Please Enter Current Password');
                return;
            }
            if (!newPassword) {
                alert('Error: Please Enter New Password');
                return;
            }
            if (!confirmPassword) {
                alert('Error: Please Enter Confirm Password');
                return;
            }
            if (newPassword !== confirmPassword) {
                alert('Error: New Password does not match Confirm Password');
                return;
            }
            if (newPassword === oldPassword) {
                alert('Error: New Password must be different from Current Password');
                return;
            }
            if (!passwordPattern.test(newPassword)) {
                alert(
                    'Please satisfy the following conditions for password:\n' +
                    'Atleast 1 capital letter\nAtleast 1 digit\nAtleast 1 special character\nAtleast 8 characters\nNo spaces'
                );
                return;
            }
            setLoading(true);
            const response = await apiClient.post('/api/v1/user/reset_password', {
                username: username, password: newPassword, old_password: oldPassword
            });
            if (response.status === 200) {
                alert('Success: ' + response.data.message);
                closeModalSuccess();
            } else {
                alert('Error: ' + response.data.message);
            }
        } catch (e) {
            alert('Error: ' + e);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={openModal} onClose={isNonDismissable ? undefined : closeModal}>
            <Box sx={styles.modalStyle}>
                <Box sx={styles.header}>
                    <Typography>Reset Password For {username}</Typography>
                    {isNonDismissable ? null : (
                        <Button onClick={closeModal} variant="outlined" color="primary">
                            <CloseIcon />
                        </Button>
                    )}
                </Box>
                <TextField
                    sx={styles.input}
                    label="Current Password"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    fullWidth
                    required
                    slotProps={{
                        htmlInput: {
                            ...protectedTextFieldProps,
                            maxLength: 30,
                        },
                    }}
                />
                <TextField
                    sx={styles.input}
                    label="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    fullWidth
                    required
                    slotProps={{
                        htmlInput: {
                            ...protectedTextFieldProps,
                            maxLength: 30,
                        },
                    }}
                />
                <TextField
                    sx={styles.input}
                    label="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    slotProps={{
                        htmlInput: {
                            ...protectedTextFieldProps,
                            maxLength: 30,
                        },
                    }}
                    fullWidth
                    required
                />
                <Button fullWidth variant="contained" color="primary" onClick={submit}>
                    {loading ? (
                        <CircularProgress color="inherit" />
                    ) : (
                        <Typography>Submit</Typography>
                    )}
                </Button>
            </Box>
        </Modal>
    );
};

export default ResetPasswordModal;
